import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import i18n from '../../i18n';

/**
 * Toasts (SPEC §30). Deliberately small: success/error/info, auto-dismissing,
 * stacked bottom-centre so they never fight the sidebar or a modal. Errors
 * that belong to a form stay inline next to the form — toasts are for
 * confirming actions whose result is not otherwise visible on screen, and for
 * offering to take one back (Undo, D-132).
 */

export type ToastTone = 'success' | 'error' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
  /** The keyboard shortcut that does the same, for `aria-keyshortcuts` (e.g. "Control+Z Meta+Z"). */
  keyshortcuts?: string;
}

export interface ToastInput {
  title: string;
  body?: string;
  tone?: ToastTone;
  /** One button, e.g. Undo (D-132). Clicking it also closes the toast. */
  action?: ToastAction;
  /**
   * How long it stays, in ms. A toast with an action stays longer by default:
   * it has to be read and reached. Hovering or focusing it holds it.
   */
  duration?: number;
  /** Called once when it goes: timed out, dismissed, or pushed off the stack by newer ones. */
  onClose?: () => void;
}

/** Shows a toast; the returned function closes it early. */
export type ShowToast = (input: ToastInput) => () => void;

interface ToastItem extends Required<Pick<ToastInput, 'title' | 'tone' | 'duration'>> {
  id: number;
  body: string | undefined;
  action: ToastAction | undefined;
  leaving: boolean;
}

const ToastContext = createContext<ShowToast | null>(null);

export function useToast(): ShowToast {
  const push = useContext(ToastContext);
  if (!push) throw new Error('useToast must be used inside <ToastProvider>');
  return push;
}

const TONE_STYLES: Record<ToastTone, { bar: string; icon: ReactNode }> = {
  success: {
    bar: 'bg-(--color-positive)',
    icon: (
      <svg viewBox="0 0 16 16" className="size-4 text-(--color-positive)" fill="none" aria-hidden>
        <path d="M3 8.5 6.5 12 13 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  error: {
    bar: 'bg-(--color-danger)',
    icon: (
      <svg viewBox="0 0 16 16" className="size-4 text-(--color-danger)" fill="none" aria-hidden>
        <path d="M8 4.5v4.5M8 11.5h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  info: {
    bar: 'bg-(--color-accent)',
    icon: (
      <svg viewBox="0 0 16 16" className="size-4 text-(--color-accent)" fill="none" aria-hidden>
        <path d="M8 7.5V11M8 4.5h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
};

const DISMISS_AFTER_MS = 4200;
export const DISMISS_WITH_ACTION_AFTER_MS = 8000;
/** Let go after being held, a toast gets at least this long again, not the half second it had left. */
const RESUME_AT_LEAST_MS = 2000;
/** The fade out; a dismissed toast stays in the DOM, inert, this long. */
export const LEAVE_MS = 200;
/** Toasts on screen at once; the oldest gives way. */
const STACK_MAX = 3;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  // The ids on screen, oldest first (the state's order), and the onClose of each.
  const shown = useRef<number[]>([]);
  const closers = useRef(new Map<number, () => void>());
  const leaving = useRef(new Set<number>());
  const leaveTimers = useRef(new Set<number>());
  // Timers stop with the provider; a page closing its own toasts as it goes
  // away (useDeletionUndo) must not start new ones after that.
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    const timers = leaveTimers.current;
    return () => {
      isMounted.current = false;
      for (const timer of timers) window.clearTimeout(timer);
      timers.clear();
    };
  }, []);

  /** Calls the toast's onClose, once, whichever way it goes. */
  const close = useCallback((id: number) => {
    const onClose = closers.current.get(id);
    closers.current.delete(id);
    onClose?.();
  }, []);

  const dismiss = useCallback(
    (id: number) => {
      if (!isMounted.current || leaving.current.has(id) || !shown.current.includes(id)) return;
      leaving.current.add(id);
      close(id);
      setToasts((current) => current.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
      const timer = window.setTimeout(() => {
        leaveTimers.current.delete(timer);
        leaving.current.delete(id);
        shown.current = shown.current.filter((other) => other !== id);
        setToasts((current) => current.filter((t) => t.id !== id));
      }, LEAVE_MS);
      leaveTimers.current.add(timer);
    },
    [close],
  );

  const push = useCallback<ShowToast>(
    (input) => {
      if (!isMounted.current) return () => undefined;
      const id = nextId.current++;
      if (input.onClose) closers.current.set(id, input.onClose);
      const evicted = shown.current.slice(0, Math.max(0, shown.current.length - (STACK_MAX - 1)));
      shown.current = [...shown.current.slice(evicted.length), id];
      for (const old of evicted) {
        leaving.current.delete(old);
        close(old);
      }
      const duration = input.duration ?? (input.action ? DISMISS_WITH_ACTION_AFTER_MS : DISMISS_AFTER_MS);
      const item: ToastItem = { id, title: input.title, body: input.body, tone: input.tone ?? 'success', action: input.action, duration, leaving: false };
      setToasts((current) => [...current.filter((t) => !evicted.includes(t.id)), item]);
      return () => dismiss(id);
    },
    [close, dismiss],
  );

  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          aria-live="polite"
          // On a phone the stack sits above the tab bar, not on its taps.
          className="pointer-events-none fixed inset-x-0 bottom-[calc(64px+env(safe-area-inset-bottom))] z-[60] flex flex-col items-center gap-2 px-4 md:bottom-6"
        >
          {toasts.map((toast) => (
            <ToastCard key={toast.id} toast={toast} onDismiss={dismiss} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

/** One toast. Its time runs only while it is neither hovered nor holding the focus (WCAG 2.2.1). */
function ToastCard({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: number) => void }) {
  const [hold, setHold] = useState({ hover: false, focus: false });
  const isHeld = hold.hover || hold.focus;
  const remaining = useRef(toast.duration);

  useEffect(() => {
    if (isHeld || toast.leaving) return;
    const startedAt = Date.now();
    const timer = window.setTimeout(() => onDismiss(toast.id), remaining.current);
    return () => {
      window.clearTimeout(timer);
      remaining.current = Math.max(RESUME_AT_LEAST_MS, remaining.current - (Date.now() - startedAt));
    };
  }, [isHeld, toast.leaving, toast.id, onDismiss]);

  return (
    <div
      role="status"
      onMouseEnter={() => setHold((h) => ({ ...h, hover: true }))}
      onMouseLeave={() => setHold((h) => ({ ...h, hover: false }))}
      onFocus={() => setHold((h) => ({ ...h, focus: true }))}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setHold((h) => ({ ...h, focus: false }));
      }}
      className={`relative flex w-full max-w-sm items-start gap-3 overflow-hidden rounded-xl bg-(--color-ink-900) py-3 pl-4 pr-3 text-white shadow-[0_16px_40px_-12px_rgb(16_24_40/0.45)] transition-opacity duration-200 ${
        toast.leaving ? 'pointer-events-none opacity-0' : 'pointer-events-auto animate-toast-in'
      }`}
    >
      <span className={`absolute inset-y-0 left-0 w-1 ${TONE_STYLES[toast.tone].bar}`} aria-hidden />
      <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-white">
        {TONE_STYLES[toast.tone].icon}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-snug">{toast.title}</p>
        {toast.body ? <p className="mt-0.5 text-xs leading-snug text-white/70">{toast.body}</p> : null}
      </div>
      {toast.action ? (
        <button
          type="button"
          aria-keyshortcuts={toast.action.keyshortcuts}
          onClick={() => {
            // Leaving, it takes no clicks; a second Enter on the focused button would still land.
            if (toast.leaving) return;
            toast.action?.onClick();
            onDismiss(toast.id);
          }}
          className="-my-0.5 shrink-0 self-center rounded-md px-2 py-1 text-sm font-semibold text-(--color-accent-line) transition-colors hover:bg-white/10 hover:text-white"
        >
          {toast.action.label}
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label={i18n.t('common.dismissNotification')}
        className="rounded-md p-1 text-white/50 transition-colors hover:bg-white/10 hover:text-white"
      >
        <svg viewBox="0 0 16 16" className="size-3.5" fill="none" aria-hidden>
          <path d="m4 4 8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}
